/**
 * Game Config Service
 * Manages game enable/disable state persisted in localStorage
 */

import { GameConfig } from '../types';

const STORAGE_KEY = 'playhub_admin_game_config';

// Master list of all games with their metadata
const ALL_GAMES: Omit<GameConfig, 'enabled' | 'status' | 'totalPlays'>[] = [
  { id: 'tic-tac-toe', name: 'Tic-Tac-Toe', icon: '⭕', playerCount: '2 Players', difficulty: 'Easy' },
  { id: 'chess', name: 'Chess', icon: '♛', playerCount: '1-2 Players', difficulty: 'Hard' },
  { id: 'checkers', name: 'Checkers', icon: '⚫', playerCount: '1-2 Players', difficulty: 'Medium' },
  { id: 'connect-four', name: 'Connect Four', icon: '🔴', playerCount: '2 Players', difficulty: 'Medium' },
  { id: 'battleship', name: 'Battleship', icon: '🚢', playerCount: '2 Players', difficulty: 'Medium' },
  { id: 'poker', name: 'Poker', icon: '🃏', playerCount: '2-8 Players', difficulty: 'Hard' },
  { id: 'mafia', name: 'Mafia', icon: '🎭', playerCount: '5-30 Players', difficulty: 'Medium' },
  { id: 'ludo', name: 'Ludo', icon: '🎲', playerCount: '2-4 Players', difficulty: 'Easy' },
  { id: 'teen-patti', name: 'Teen Patti', icon: '🂡', playerCount: '3-6 Players', difficulty: 'Medium' },
  { id: 'carrom', name: 'Carrom', icon: '🎯', playerCount: '2 Players', difficulty: 'Medium' },
  { id: 'snakes-and-ladders', name: 'Snakes & Ladders', icon: '🐍', playerCount: '2-4 Players', difficulty: 'Easy' },
  { id: 'pachisi', name: 'Pachisi', icon: '🐚', playerCount: '2-4 Players', difficulty: 'Medium' },
  { id: 'reversi', name: 'Reversi', icon: '⚪', playerCount: '2 Players', difficulty: 'Medium' },
  { id: 'mancala', name: 'Mancala', icon: '🫘', playerCount: '2 Players', difficulty: 'Medium' },
  { id: 'go', name: 'Go', icon: '⬛', playerCount: '2 Players', difficulty: 'Hard' },
  { id: 'backgammon', name: 'Backgammon', icon: '🎰', playerCount: '2 Players', difficulty: 'Medium' },
  { id: 'uno', name: 'UNO', icon: '🟥', playerCount: '2-4 Players', difficulty: 'Easy' },
  { id: 'sudoku', name: 'Sudoku', icon: '🔢', playerCount: '1 Player', difficulty: 'Medium' },
  { id: '2048', name: '2048', icon: '🧮', playerCount: '1 Player', difficulty: 'Easy' },
  { id: 'minesweeper', name: 'Minesweeper', icon: '💣', playerCount: '1 Player', difficulty: 'Medium' },
  { id: 'rummy', name: 'Rummy', icon: '🃏', playerCount: '2-6 Players', difficulty: 'Medium' },
  { id: 'solitaire', name: 'Solitaire', icon: '🂠', playerCount: '1 Player', difficulty: 'Easy' },
  { id: 'dots-and-boxes', name: 'Dots & Boxes', icon: '⬜', playerCount: '2-4 Players', difficulty: 'Easy' },
  { id: 'hangman', name: 'Hangman', icon: '📝', playerCount: '1 Player', difficulty: 'Easy' },
  { id: 'pinball', name: 'Pinball', icon: '🕹️', playerCount: '1 Player', difficulty: 'Medium' },
  { id: 'chaupar', name: 'Chaupar', icon: '🏰', playerCount: '4 Players (Teams)', difficulty: 'Hard' },
  { id: 'wordle', name: 'Daily Word', icon: '🟩', playerCount: '1 Player', difficulty: 'Medium' },
  { id: 'word-game', name: 'Word Clash', icon: '📝', playerCount: '1-2 Players', difficulty: 'Medium' },
  { id: 'yahtzee', name: 'Yahtzee', icon: '🎲', playerCount: '1-2 Players', difficulty: 'Medium' },
  { id: 'billiards', name: '8-Ball Pool', icon: '🎱', playerCount: '1-2 Players', difficulty: 'Medium' },
  { id: 'trivia', name: 'Trivia Quiz', icon: '🧠', playerCount: '1-2 Players', difficulty: 'Medium' },
];

export class GameConfigService {
  private static instance: GameConfigService | null = null;

  static getInstance(): GameConfigService {
    if (!GameConfigService.instance) {
      GameConfigService.instance = new GameConfigService();
    }
    return GameConfigService.instance;
  }

  /**
   * Get all game configs with enable/disable state and play counts
   */
  getGameConfigs(): GameConfig[] {
    const disabledGames = this.getDisabledGames();
    const playCounts = this.getPlayCounts();

    return ALL_GAMES.map(game => ({
      ...game,
      enabled: !disabledGames.has(game.id),
      status: disabledGames.has(game.id) ? 'disabled' as const : 'available' as const,
      totalPlays: playCounts[game.id] || 0,
    }));
  }

  /**
   * Toggle a game's enabled state
   */
  setGameEnabled(gameId: string, enabled: boolean): void {
    const disabledGames = this.getDisabledGames();
    if (enabled) {
      disabledGames.delete(gameId);
    } else {
      disabledGames.add(gameId);
    }
    this.saveDisabledGames(disabledGames);
  }

  /**
   * Get set of disabled game IDs
   */
  getDisabledGames(): Set<string> {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return new Set();
      const data = JSON.parse(stored);
      return new Set(data.disabledGames || []);
    } catch {
      return new Set();
    }
  }

  /**
   * Get play counts from game sessions in localStorage
   */
  private getPlayCounts(): Record<string, number> {
    try {
      const stored = localStorage.getItem('playhub_game_sessions');
      if (!stored) return {};
      const sessions = JSON.parse(stored) as Array<{ gameType: string; completed: boolean }>;
      const counts: Record<string, number> = {};
      sessions.forEach(s => {
        if (s.completed) {
          counts[s.gameType] = (counts[s.gameType] || 0) + 1;
        }
      });
      return counts;
    } catch {
      return {};
    }
  }

  private saveDisabledGames(disabledGames: Set<string>): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        disabledGames: Array.from(disabledGames),
        updatedAt: Date.now()
      }));
    } catch {
      // Ignore storage errors
    }
  }
}
